import axios from 'axios';
import {
  SystemStatus,
  ServiceHealth,
  DatabaseHealth,
  ExternalAPIHealth,
  SystemMetrics,
  ServiceMetrics,
  Alert,
  MetricsHistory,
} from '../types/healthMonitor';

function resolveHealthUrl(): string {
  const envVar = process.env.REACT_APP_HEALTH_MONITOR_URL;
  if (envVar && !envVar.includes('localhost')) return envVar;
  const hostname = window.location.hostname;
  const isProduction = window.location.protocol === 'https:' || (hostname !== 'localhost' && hostname !== '127.0.0.1');
  if (isProduction) return '/api/v1';
  return `http://${hostname}:8095/api/v1`;
}

const API_BASE_URL = resolveHealthUrl();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const healthMonitorService = {
  // System Status (Public)
  async getSystemStatus(): Promise<SystemStatus> {
    const response = await api.get('/system/status');
    return response.data;
  },

  // Services Health
  async getServicesHealth(): Promise<{ services: ServiceHealth[]; total: number }> {
    const response = await api.get('/health/services');
    return response.data;
  },

  async getServiceHealth(serviceName: string): Promise<ServiceHealth> {
    const response = await api.get(`/health/services/${serviceName}`);
    return response.data;
  },

  async triggerHealthCheck(serviceName: string): Promise<{ message: string; service: string }> {
    const response = await api.post(`/health/check/${serviceName}`);
    return response.data;
  },

  // Databases Health
  async getDatabasesHealth(): Promise<{ databases: DatabaseHealth[]; total: number }> {
    const response = await api.get('/health/databases');
    return response.data;
  },

  // External APIs Health
  async getExternalAPIsHealth(): Promise<{ external_apis: ExternalAPIHealth[]; total: number }> {
    const response = await api.get('/health/external-apis');
    return response.data;
  },

  // System Metrics
  async getSystemMetrics(): Promise<SystemMetrics> {
    const response = await api.get('/metrics/system');
    return response.data;
  },

  async getServicesMetrics(): Promise<{ services: Record<string, ServiceMetrics>; total: number }> {
    const response = await api.get('/metrics/services');
    return response.data;
  },

  async getMetricsHistory(since: string = '1h'): Promise<MetricsHistory> {
    const response = await api.get('/metrics/history', {
      params: { since },
    });
    return response.data;
  },

  // Alerts
  async getAlerts(): Promise<{ alerts: Alert[]; total: number }> {
    const response = await api.get('/alerts');
    return response.data;
  },

  async getAlert(alertId: string): Promise<Alert> {
    const response = await api.get(`/alerts/${alertId}`);
    return response.data;
  },

  async getActiveAlerts(): Promise<{ alerts: Alert[]; total: number }> {
    const response = await api.get('/alerts/active');
    return response.data;
  },

  async acknowledgeAlert(alertId: string): Promise<{ message: string; alert_id: string }> {
    const response = await api.put(`/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  async resolveAlert(alertId: string): Promise<{ message: string; alert_id: string }> {
    const response = await api.put(`/alerts/${alertId}/resolve`);
    return response.data;
  },
};
